import { map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import {
  HttpClient,
  HttpEventType,
  HttpRequest,
  HttpHeaders,
} from "@angular/common/http";
import { ConfigService } from "../config/config.service";
import {
  INews,
  INewsResponse,
  IParts,
  ISetEntityPublishedStatus,
  NewsOperationType,
  PartImage,
  PartType,
} from "../models/news.model";
import { UserModel } from "../models/user.model";
import { AttachmentModel, UploadType } from "../models/document.model";
import { marked } from "marked";
import { AppState } from "../store/app.state";
import { Store } from "@ngrx/store";
import { UpdateNewsSuccessAction } from "../store/news/actions";

const defaultHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

@Injectable()
export class NewsService {
  constructor(
    protected configService: ConfigService,
    protected httpClient: HttpClient,
    protected store: Store<AppState>,
  ) {}

  private _baseUrl = this.configService.get("remoteContentApiUrl");

  public getAllNews(urlOrQueryParams: string): Observable<INewsResponse> {
    const url = this.getUrl(urlOrQueryParams);
    return this.httpClient
      .get<INewsResponse>(url, { headers: defaultHeaders })
      .pipe(map((res) => ({ ...res, currentUrl: url })));
  }

  public getPublishedNews(urlOrQueryParams: string): Observable<INewsResponse> {
    const url = this.getUrl(urlOrQueryParams, true);
    return this.httpClient
      .get<INewsResponse>(url, { headers: defaultHeaders })
      .pipe(map((res) => ({ ...res, currentUrl: url })));
  }

  public patchData(id: string, patchData: Partial<INews>): Observable<any> {
    const url = `${this._baseUrl}news/${id}`;
    return this.httpClient
      .patch(url, patchData, { headers: defaultHeaders })
      .pipe(map((res) => res));
  }

  public setPublishedStatus(
    id: string,
    publishedStatus: Partial<ISetEntityPublishedStatus>,
  ): Observable<any> {
    const url = `${this._baseUrl}news/${id}/publish`;
    return this.httpClient
      .post(url, publishedStatus, { headers: defaultHeaders })
      .pipe(map((res) => res));
  }

  public archiveNews(id: string): Observable<any> {
    const url = `${this._baseUrl}news/${id}/archive`;
    return this.httpClient
      .patch(url, {}, { headers: defaultHeaders })
      .pipe(map((res) => res));
  }

  public createOrUpdate(
    files: Set<File> | null | undefined,
    data: INews,
    type: NewsOperationType,
  ): { [key: string]: { progress: Observable<number> } } {
    const url = `${this._baseUrl}news${type === NewsOperationType.Put ? `/${data.id}` : ""}`;
    const formData = new FormData();
    const file = this.getFirstFile(files);

    if (file?.type) formData.append("Photo", file, file.name);
    formData.append("News", JSON.stringify(data));

    const req = new HttpRequest(
      type === NewsOperationType.Put ? "PUT" : "POST",
      url,
      formData,
      {
        reportProgress: true,
      },
    );

    return this.sendWithProgress(req, file?.name || "news");
  }

  clone(
    id: string,
    currentUser: UserModel,
  ): { [key: string]: { progress: Observable<number> } } {
    const url = `${this._baseUrl}News/${id}/clone`;

    const body = {
      CreatedBy: currentUser.firstName,
      CreatedById: currentUser.email,
    };

    const req = new HttpRequest("POST", url, body, {
      reportProgress: true,
      headers: new HttpHeaders(defaultHeaders),
    });

    return this.sendWithProgress(req, "news");
  }

  public getNewsById(
    id: string,
    published: boolean = false,
  ): Observable<INews> {
    const url = `${this._baseUrl}news/${published ? "by-id/" : ""}${id}`;
    return this.httpClient
      .get<INews>(url, { headers: defaultHeaders })
      .pipe(map((res) => res));
  }

  private getUrl(urlOrQueryParams: string, published: boolean = false): string {
    return urlOrQueryParams.startsWith("http")
      ? urlOrQueryParams
      : `${this._baseUrl}news${published ? "/published" : ""}${urlOrQueryParams}`;
  }

  private getFirstFile(files: Set<File> | null | undefined): File | null {
    if (!files || files.size === 0) return null;
    // Convert iterator to first value quickly
    const it = files.values();
    const first = it.next();
    return first && !first.done ? first.value : null;
  }

  private sendWithProgress(
    req: HttpRequest<any>,
    statusKey: string,
  ): { [key: string]: { progress: Observable<number> } } {
    const status: { [key: string]: { progress: Observable<number> } } = {};
    const progress = new Subject<number>();

    this.httpClient.request(req).subscribe({
      next: (response) => {
        if (response.type === HttpEventType.UploadProgress) {
          // Protect against division by zero
          const percentDone = response.total
            ? Math.round((100 * response.loaded) / response.total)
            : 0;
          progress.next(percentDone);
        } else if (response.type === HttpEventType.Response) {
          // Treat any 2xx response as success
          if (response.status >= 200 && response.status < 300) {
            this.store.dispatch(new UpdateNewsSuccessAction(response.body));
            progress.complete();
          }
        }
      },
      error: (error) => {
        progress.error(error);
      },
    });

    status[statusKey] = { progress: progress.asObservable() };
    return status;
  }

  deleteThumbnail(fileId: string, newsEntityId: string): Observable<any> {
    const url = `${this._baseUrl}News/${newsEntityId}/photos/${fileId}`;
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    return this.httpClient.delete(url, { headers: headers }).pipe(
      map((res) => {
        return res;
      }),
    );
  }

  getAttachments(parts: IParts[]): AttachmentModel[] {
    return parts
      ?.filter((part) => part.type === PartType.Document)
      .map((part) => part.document);
  }

  async parseMarkdownContent(parts: IParts[]): Promise<string> {
    const markdownParts =
      parts
        ?.filter((part) => part.type === PartType.Markdown)
        .map((part) => part.content)
        .join("\n") || "";

    return marked.parse(markdownParts);
  }

  getThumbnailImage(parts: IParts[]): PartImage | null {
    return (
      parts?.filter((part) => part.type === PartType.Photo)[0]?.image || null
    );
  }
}
